// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import JSZip from 'jszip'
import { removeItem } from '../../../store/slices/canvasSlice'
import { saveNode, unsaveNode } from '../../../store/slices/savedNodesSlice'
import type { CanvasStateService } from '../services/CanvasStateService'
import type { AppDispatch } from '../../../store/store'

interface UseBulkActionsParams {
    selectedNodeIds: string[]
    canvasItems: Array<{ id: string; src: string; mediaType?: string }>
    sessionId: string | null
    dispatch: AppDispatch
    savedNodeIds: Record<string, boolean>
    hideAll: (ids: string[]) => void
    stateService: CanvasStateService
    clearLocalSelection: () => void
    resetFraming: () => void
}

export function useBulkActions(params: UseBulkActionsParams) {
    const {
        selectedNodeIds,
        canvasItems,
        sessionId,
        dispatch,
        savedNodeIds,
        hideAll,
        stateService,
        clearLocalSelection,
        resetFraming,
    } = params

    const handleBulkDownload = useCallback(async () => {
        const items = canvasItems.filter((i) => selectedNodeIds.includes(i.id))
        if (items.length === 0) return

        if (items.length === 1) {
            // Single file download
            const item = items[0]
            try {
                const response = await fetch(item.src)
                const blob = await response.blob()
                const blobUrl = URL.createObjectURL(blob)
                const a = document.createElement('a')
                const urlPath = new URL(item.src, window.location.href).pathname
                a.href = blobUrl
                a.download = urlPath.split('/').pop() || 'image.png'
                document.body.appendChild(a)
                a.click()
                a.remove()
                URL.revokeObjectURL(blobUrl)
            } catch {
                window.open(item.src, '_blank', 'noopener,noreferrer')
            }
            return
        }

        // Multi-file zip download
        try {
            const zip = new JSZip()
            const fetches = items.map(async (item, index) => {
                const response = await fetch(item.src)
                const blob = await response.blob()
                const ext = item.mediaType === 'video' ? '.mp4' : '.png'
                const urlPath = new URL(item.src, window.location.href).pathname
                const baseName = urlPath.split('/').pop() || `image-${index}${ext}`
                zip.file(baseName, blob)
            })
            await Promise.all(fetches)
            const zipBlob = await zip.generateAsync({ type: 'blob' })
            const url = URL.createObjectURL(zipBlob)
            const a = document.createElement('a')
            a.href = url
            a.download = `aether-studio-${Date.now()}.zip`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Bulk download failed:', err)
        }
    }, [selectedNodeIds, canvasItems])

    const handleBulkSave = useCallback(() => {
        if (selectedNodeIds.length === 0) return
        const allSaved = selectedNodeIds.every((id) => savedNodeIds[id])
        for (const id of selectedNodeIds) {
            if (allSaved) {
                dispatch(unsaveNode(id))
            } else {
                if (!savedNodeIds[id]) dispatch(saveNode(id))
            }
        }
    }, [selectedNodeIds, savedNodeIds, dispatch])

    const handleBulkRemove = useCallback(() => {
        if (!sessionId || selectedNodeIds.length === 0) return
        hideAll(selectedNodeIds)
        for (const id of selectedNodeIds) {
            dispatch(removeItem({ sessionId, itemId: id }))
        }
        stateService.clearSelection(sessionId)
        clearLocalSelection()
        resetFraming()
    }, [selectedNodeIds, sessionId, dispatch, hideAll, stateService, clearLocalSelection, resetFraming])

    return { handleBulkDownload, handleBulkSave, handleBulkRemove }
}
